import React, { useRef, useEffect, useContext, useState } from "react";
import styles from "./GroupPhotos.module.scss";
import TinySlider from "tiny-slider-react";
import sliderStyles from "./TinySlider.module.scss";
import { animOnSwipe } from "../utils/animation";
import { isMobile, isIOS } from "react-device-detect";
import { Context } from "../Store";
import { onPreviewChange } from "../utils/storyUIHandlers";
import { ReactComponent as LightbulbsRow } from "../img/lightbulbs-row.svg";
import { ReactComponent as LightbulbsRowMob } from "../img/lightbulbs-row-mob.svg";
import ShareArrow from "./ShareArrow";
import { AzureMP } from "react-azure-mp";
import ReactPlayer from "react-player/lazy";

const GroupPhotos = (props) => {
  const [state] = useContext(Context);
  const photosSliderRef = useRef();
  const loopcamsSliderRef = useRef();
  const tabsRef = useRef(null);
  const tabSliderRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loopcamIndex, setLoopcamIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("photos");
  const [hoveredLoopcamIndex, setHoveredLoopcamIndex] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.sectionId === "group-photos" && e.detail?.tab) {
        setActiveTab(e.detail.tab);
      }
    };
    window.addEventListener("storyTabChange", handler);
    return () => window.removeEventListener("storyTabChange", handler);
  }, []);

  useEffect(() => {
    return onPreviewChange((preview) => {
      if (!preview || preview.section !== "LoopCams") {
        setHoveredLoopcamIndex(null);
      } else {
        setHoveredLoopcamIndex(preview.index);
      }
    });
  }, []);

  const photos = state.photos || [];
  const loopcams = state.loopcams || [];
  const sliderPhotos = photos.slice(1);

  const hasPhotos = photos.length > 0;
  const hasLoopcams = loopcams.length > 0;
  // Tabs appear when both slider photos AND loopcams have content
  const showTabs = sliderPhotos.length > 0 && hasLoopcams;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentIndex(0);
  };

  const updateTabSlider = (tab) => {
    if (!tabsRef.current || !tabSliderRef.current) return;
    const activeBtn = tabsRef.current.querySelector(`[data-tab="${tab}"]`);
    if (!activeBtn) return;
    tabSliderRef.current.style.width = `${activeBtn.offsetWidth - 1}px`;
    tabSliderRef.current.style.transform = `translateX(${activeBtn.offsetLeft}px)`;
  };

  useEffect(() => {
    updateTabSlider(activeTab);
  }, [activeTab]);

  // Which slider to show
  const showPhotosSlider = showTabs ? activeTab === "photos" : sliderPhotos.length > 0;
  const showLoopcamsSlider = showTabs ? activeTab === "loopcams" : hasLoopcams && sliderPhotos.length === 0;

  // ── Photo data ──────────────────────────────────────────────────────────────

  const groupPhotoSources = photos.map((photo) => state.bloburl + "/groupphotos/" + photo.asset_uri);
  const groupPhotoMedia = photos.map((photo) => ({
    mediaType: "PHOTO",
    mediaURI: photo.asset_uri,
    downloadURI: state.bloburl + "/groupphotos/" + photo.asset_uri,
    sectionName: "Group Photo",
  }));

  const photoSettings = {
    lazyload: true,
    touch: true,
    nav: false,
    controls: false,
    mouseDrag: true,
    loop: sliderPhotos.length >= 3,
    items: 1,
    gutter: 15,
    edgePadding: 0,
    responsive: {
      640: { items: Math.min(sliderPhotos.length, 2), gutter: 15, edgePadding: 0 },
      1024: {
        items: sliderPhotos.length >= 3 ? 1 : Math.min(sliderPhotos.length, 2),
        gutter: 20,
        edgePadding: sliderPhotos.length >= 3 ? 200 : 0,
      },
      1440: {
        items: sliderPhotos.length >= 3 ? 1 : Math.min(sliderPhotos.length, 2),
        gutter: 20,
        edgePadding: sliderPhotos.length >= 3 ? 200 : 0,
      },
      1600: {
        items: sliderPhotos.length >= 3 ? 3 : Math.min(sliderPhotos.length, 2),
        gutter: 20,
        edgePadding: sliderPhotos.length >= 3 ? 120 : 0,
      },
    },
  };

  // ── Loopcam data ─────────────────────────────────────────────────────────────

  const showControls = isIOS;
  const loopcamVideos = loopcams.map((video) => {
    if (video.video_provider === "Azure") {
      return (
        <div className={styles.loopcamItem}>
          <AzureMP
            options={{ controls: showControls, autoplay: true, playsInline: true, muted: true }}
            src={[{ src: video.video_url, type: "application/vnd.ms-sstr+xml" }]}
          />
        </div>
      );
    } else if (video.video_provider === "AzureBlob") {
      return (
        <div className={styles.loopcamItem}>
          <ReactPlayer
            className="react-player"
            url={video.video_url}
            muted
            controls
            playing
            width="100%"
            height="100%"
          />
        </div>
      );
    } else {
      return (
        <iframe
          id={"loopcam-" + video.asset_uri}
          title={"Loop cam " + video.asset_uri}
          src={`https://player.vimeo.com/video/${video.asset_uri}?title=0&portait=0&byline=0&loop=true&api=1&playsinline=true&player_id=video${video.asset_uri}`}
          frameBorder="0"
          width="1920px"
          height="1080px"
        />
      );
    }
  });

  const loopcamMedia = loopcams.map((video) => ({
    mediaType: "VIDEO",
    mediaURI: video.asset_uri,
    mediaProvider: video.video_provider,
    videoURL: video.video_url,
    sectionName: "Loop Cams",
    thumbnail: video.thumbnail_url,
  }));

  const loopcamSettings = {
    lazyload: true,
    touch: true,
    nav: false,
    controls: false,
    mouseDrag: true,
    loop: loopcams.length >= 3,
    items: 1,
    gutter: 15,
    edgePadding: 0,
    responsive: {
      640: { items: Math.min(loopcams.length, 2), gutter: 15, edgePadding: 0 },
      1024: {
        items: 1,
        gutter: 20,
        edgePadding: loopcams.length >= 3 ? 280 : loopcams.length === 2 ? 0 : 0,
      },
      1440: {
        items: 1,
        gutter: 20,
        edgePadding: loopcams.length >= 3 ? 420 : 0,
      },
      1600: {
        items: loopcams.length >= 3 ? 1 : Math.min(loopcams.length, 2),
        gutter: 20,
        edgePadding: loopcams.length >= 3 ? 500 : 0,
      },
    },
  };

  // ── Effects ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!photosSliderRef.current) return;
    const slider = photosSliderRef.current.slider;
    animOnSwipe(slider);
    const updatePosition = (info) => setCurrentIndex((info.displayIndex ?? info.index + 1) - 1);
    updatePosition(slider.getInfo());
    slider.events.on("indexChanged", updatePosition);
    // Handle clicks on cloned slides (loop mode) — clones aren't in React's fiber tree
    // tiny-slider marks clones with .tns-slide-cloned so we skip real slides
    const container = slider.getInfo().container;
    const handleCloneClick = (e) => {
      if (!e.target.closest(".tns-slide-cloned")) return;
      const target = e.target.closest("[data-photo-index]");
      if (!target) return;
      const idx = parseInt(target.getAttribute("data-photo-index"), 10);
      openPhotoLightbox(idx + 1);
    };
    container.addEventListener("click", handleCloneClick);
    return () => {
      if (slider?.events) slider.events.off("indexChanged", updatePosition);
      container.removeEventListener("click", handleCloneClick);
    };
  }, [photosSliderRef, showPhotosSlider]);

  useEffect(() => {
    if (!loopcamsSliderRef.current) return;
    const slider = loopcamsSliderRef.current.slider;
    if (!slider) return;
    try {
      animOnSwipe(slider);
    } catch (_) {}
    const updatePosition = (info) => setLoopcamIndex((info.displayIndex ?? info.index + 1) - 1);
    updatePosition(slider.getInfo());
    slider.events.on("indexChanged", updatePosition);
    // Handle clicks on cloned slides (loop mode) — clones aren't in React's fiber tree
    // tiny-slider marks clones with .tns-slide-cloned so we skip real slides
    const container = slider.getInfo().container;
    const handleCloneClick = (e) => {
      if (!e.target.closest(".tns-slide-cloned")) return;
      const target = e.target.closest("[data-loopcam-index]");
      if (!target) return;
      const idx = parseInt(target.getAttribute("data-loopcam-index"), 10);
      openLoopcamLightbox(idx);
    };
    container.addEventListener("click", handleCloneClick);
    return () => {
      if (slider?.events) slider.events.off("indexChanged", updatePosition);
      container.removeEventListener("click", handleCloneClick);
    };
  }, [loopcamsSliderRef, showLoopcamsSlider, activeTab]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const openPhotoLightbox = (index) => {
    props.openLightbox({
      section: "GroupPhoto",
      index,
      sources: groupPhotoSources,
      mediashare: groupPhotoMedia,
      type: "image",
    });
  };

  const handleLoopcamHover = (index, e) => {
    props.handleHover(
      {
        section: "LoopCams",
        index,
        ...loopcamMedia[index],
        target: e.currentTarget,
        timeStamp: e.timeStamp,
        groupLength: loopcamMedia.length,
        sources: loopcamVideos,
        mediashare: loopcamMedia,
      },
      e.type,
    );
  };

  const openLoopcamLightbox = (index) => {
    props.openLightbox({ section: "LoopCams", index, sources: loopcamVideos, mediashare: loopcamMedia });
  };

  if (!hasPhotos && !hasLoopcams) return null;

  return (
    <div
      id="group-photos"
      className={`section scale-slider ${styles.groupPhotosSection}`}
      style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/img/${isMobile ? "photosBgMob.png" : "photosBg.png"})` }}
    >
      <h3>You were all there</h3>
      <h2>Wall of fame</h2>

      {hasPhotos && (
        <>
          <div className={styles.photoFrameContainer}>
            <div onClick={() => openPhotoLightbox(0)} style={{ cursor: "pointer" }}>
              <img
                src={photos[0] ? state.bloburl + "/groupphotos/" + photos[0].asset_uri : ""}
                alt="group"
                className={styles.photoFramePhoto}
              />
            </div>
            <ShareArrow
              title="Flight Club Stories - Group Photo"
              url={state.bloburl + "/groupphotos/" + photos[0].asset_uri}
              text="Check out this group photo from our Flight Club visit."
              downloadUrl={state.bloburl + "/groupphotos/" + photos[0].asset_uri}
              isVideo={false}
              customBottom="-30px"
              sectionName="Group Photo"
              mediaShare={{
                mediaType: "PHOTO",
                mediaURI: photos[0].asset_uri,
                downloadURI: state.bloburl + "/groupphotos/" + photos[0].asset_uri,
                sectionName: "Group Photo",
              }}
              cityurl={state.cityurl}
              venue={state.venue}
              guid={state.guid}
              country={state.country}
            />
            <img
              src={`${process.env.PUBLIC_URL}/img/photo-frame.png`}
              alt="frame"
              aria-hidden="true"
              className={styles.photoFrameOverlay}
            />
          </div>
          <div className={styles.lightbulbsRow}>{isMobile ? <LightbulbsRowMob /> : <LightbulbsRow />}</div>
        </>
      )}

      {showTabs && (
        <div className={styles.tabsWrapper}>
          <div className={styles.tabs} ref={tabsRef}>
            <span className={styles.tabSlider} ref={tabSliderRef} />
            <button
              data-tab="photos"
              className={`${styles.tab}${activeTab === "photos" ? ` ${styles.tabActive}` : ""}`}
              onClick={() => handleTabChange("photos")}
            >
              Photos
            </button>
            <button
              data-tab="loopcams"
              className={`${styles.tab}${activeTab === "loopcams" ? ` ${styles.tabActive}` : ""}`}
              onClick={() => handleTabChange("loopcams")}
            >
              Loops
            </button>
          </div>
        </div>
      )}

      {sliderPhotos.length > 0 && (
        <div className={showTabs && activeTab !== "photos" ? styles.tabContentHidden : undefined}>
          <p className={styles.sliderSubtitle}>Your best poses.</p>
          <div
            className={`${styles.sliderContainer}${sliderPhotos.length === 2 ? ` ${styles.sliderTwoUp}` : ""}`}
            style={
              !isMobile && sliderPhotos.length < 3
                ? { maxWidth: sliderPhotos.length === 1 ? "711px" : "1200px", margin: "0 auto" }
                : undefined
            }
          >
            {!isMobile && sliderPhotos.length >= 3 && <div className={styles.photosPeekBlockerLeft} />}
            {!isMobile && sliderPhotos.length >= 3 && <div className={styles.photosPeekBlockerRight} />}
            <TinySlider settings={photoSettings} ref={photosSliderRef}>
              {sliderPhotos.map((photo, index) => (
                <div
                  key={index}
                  style={{ position: "relative" }}
                  className={[sliderStyles.slide, styles.groupPhotoItem].join(" ")}
                >
                  <div
                    data-photo-index={index}
                    onClick={() => openPhotoLightbox(index + 1)}
                    style={{ cursor: "pointer" }}
                  >
                    <img src={state.bloburl + "/groupphotos/" + photo.asset_uri} alt="" />
                  </div>
                  <ShareArrow
                    title="Flight Club Stories - Group Photo"
                    url={state.bloburl + "/groupphotos/" + photo.asset_uri}
                    text="Check out this group photo from our Flight Club visit."
                    downloadUrl={state.bloburl + "/groupphotos/" + photo.asset_uri}
                    isVideo={false}
                    customBottom={isMobile ? "-36px" : "-43px"}
                    sectionName="Group Photo"
                    mediaShare={{
                      mediaType: "PHOTO",
                      mediaURI: photo.asset_uri,
                      downloadURI: state.bloburl + "/groupphotos/" + photo.asset_uri,
                      sectionName: "Group Photo",
                    }}
                    cityurl={state.cityurl}
                    venue={state.venue}
                    guid={state.guid}
                    country={state.country}
                  />
                </div>
              ))}
            </TinySlider>
          </div>
          {sliderPhotos.length >= 3 && (
            <div className={styles.counterRow}>
              <button
                className={styles.counterArrow}
                onClick={() => photosSliderRef.current?.slider?.goTo("prev")}
                aria-label="Previous"
              >
                <img src="./img/slide-arrow-prev.svg" alt="" />
              </button>
              <div className={styles.slideCounter}>
                <span>{currentIndex + 1}</span>/{sliderPhotos.length}
              </div>
              <button
                className={styles.counterArrow}
                onClick={() => photosSliderRef.current?.slider?.goTo("next")}
                aria-label="Next"
              >
                <img src="./img/slide-arrow-next.svg" alt="" />
              </button>
            </div>
          )}
        </div>
      )}

      {hasLoopcams && (
        <div className={showTabs && activeTab !== "loopcams" ? styles.tabContentHidden : undefined}>
          <p className={styles.sliderSubtitle}>Your best moves.</p>
          <div
            className={`${styles.sliderContainer}${loopcams.length === 2 ? ` ${styles.sliderTwoUp}` : ""}`}
            style={
              !isMobile && loopcams.length < 3
                ? { maxWidth: loopcams.length === 1 ? "711px" : "1200px", margin: "0 auto" }
                : undefined
            }
          >
            {!isMobile && loopcams.length >= 3 && <div className={styles.photosPeekBlockerLeft} />}
            {!isMobile && loopcams.length >= 3 && <div className={styles.photosPeekBlockerRight} />}
            <TinySlider settings={loopcamSettings} ref={loopcamsSliderRef}>
              {loopcams.map((video, index) => (
                <div
                  key={index}
                  style={{ position: "relative" }}
                  className={[sliderStyles.slide, styles.loopcamItem].join(" ")}
                >
                  <div
                    data-loopcam-index={index}
                    onClick={() => openLoopcamLightbox(index)}
                    onMouseEnter={isMobile ? () => false : handleLoopcamHover.bind(this, index)}
                    onMouseLeave={handleLoopcamHover.bind(this, index)}
                    style={{ cursor: "pointer" }}
                  >
                    {video.thumbnail_url ? (
                      <img src={video.thumbnail_url} alt="" className={styles.loopcamThumb} />
                    ) : (
                      <img
                        src={`${process.env.PUBLIC_URL}/img/Stories-Loop-Cam-graphic.png`}
                        alt=""
                        className={styles.loopcamThumb}
                      />
                    )}
                  </div>
                  {hoveredLoopcamIndex !== index && (
                    <ShareArrow
                      title="Flight Club Stories - Loopcam"
                      url={video.video_url}
                      text="Check out this loopcam from our Flight Club visit."
                      downloadUrl={video.video_url}
                      isVideo={true}
                      customBottom={isMobile ? "-36px" : "-43px"}
                      sectionName="Loop Cams"
                      mediaShare={{
                        mediaType: "VIDEO",
                        mediaURI: video.asset_uri,
                        videoURL: video.video_url,
                        sectionName: "LoopCams",
                        thumbnail: video.thumbnail_url,
                      }}
                      cityurl={state.cityurl}
                      venue={state.venue}
                      azureurl={state.azureurl}
                      videourl={state.videourl}
                      guid={state.guid}
                      country={state.country}
                    />
                  )}
                </div>
              ))}
            </TinySlider>
          </div>
          {loopcams.length >= 3 && (
            <div className={styles.counterRow}>
              <button
                className={styles.counterArrow}
                onClick={() => loopcamsSliderRef.current?.slider?.goTo("prev")}
                aria-label="Previous"
              >
                <img src="./img/slide-arrow-prev.svg" alt="" />
              </button>
              <div className={styles.slideCounter}>
                <span>{loopcamIndex + 1}</span>/{loopcams.length}
              </div>
              <button
                className={styles.counterArrow}
                onClick={() => loopcamsSliderRef.current?.slider?.goTo("next")}
                aria-label="Next"
              >
                <img src="./img/slide-arrow-next.svg" alt="" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GroupPhotos;
